import { SegmentCollection } from '@ve-pro/types/track.type'
import { isTransition } from './segment-transition'
import { isSegmentFragment, isSegmentFragmentChild } from './segment-fragment'
import { isConvertibleToSegmentGroup, isSegmentGroup } from './segment-group'
import {
  isSegmentDubbing,
  isSegmentSubtitle,
  isSegmentSubtitleGroup,
} from './segment-subtitle'
import {
  isTemplateMainVideoSegment,
  isTemplateSegment,
} from '../tracks-actions'
import { SegmentType } from '@ve-pro/enums/segment'
import { AssetType } from '@ve-pro/enums/asset'

enum SegmentBehaviorName {
  // 选中
  Select = 'select',
  // 批量选中（全选/框选/多选）
  SelectAll = 'selectAll',
  // 删除
  Delete = 'delete',
  // 裁剪
  Cut = 'cut',
  // 拖拽
  Drag = 'drag',
  // 复制
  Copy = 'copy',
  // 粘贴
  Paste = 'paste',
  // 分割
  Split = 'split',
  // 设置为元件组
  ToGroup = 'toGroup',
  // 移入片段
  MoveIn = 'moveIn',
  // 移出片段
  MoveOut = 'moveOut',
  // 设为场景
  ToFragment = 'toFragment',
  // 分离音频/还原音频
  SeparateAudio = 'separateAudio',
  // 变速
  Rate = 'rate',
  // 动画
  Animation = 'animation',

  // 缩放、位置大小、旋转、不透明度
  Rect = 'rect',

  // 阴影
  Shadow = 'shadow',

  // 水印
  Watermark = 'watermark',

  /**
   * 文本设置：
   * 文本输入
   * 字体
   * 颜色
   * 字号
   * 字距
   * 行高
   * BIU
   * 自适应
   * 文本对齐方式
   * 预设样式
   * 描边
   */
  TextStyle = 'textStyle',

  // 音频
  AudioSetting = 'audioSetting',
  // 声音
  AudioVoice = 'audioVoice',
  // 淡入淡出
  AudioFade = 'audioFade',

  // 滤镜
  FilterSetting = 'filterSetting',

  // 抠图
  Matting = 'matting',

  // TODO: 补充下面各个标识的逻辑
  // replaceAsset	替换资产(目前所有轨道都支持，可以延后处理)

  // asr	识别字幕

  // speech	配音
}

type BehaviorValidator = (segment: SegmentCollection) => boolean

interface SegmentBehaviorOption {
  enabled: boolean
}

type SegmentBehaviorOptionValueGetter<R = unknown> = (
  segment: SegmentCollection,
) => R
type DefineBehaviorOption<Option extends SegmentBehaviorOption> = {
  [key in keyof Option]: SegmentBehaviorOptionValueGetter<Option[key]>
}

export type SegmentBehavior = Partial<
  Record<SegmentBehaviorName, SegmentBehaviorOption>
>

const Behaviors: Partial<
  Record<
    SegmentBehaviorName,
    {
      option: DefineBehaviorOption<SegmentBehaviorOption>
      validator?: BehaviorValidator
    }
  >
> = {
  [SegmentBehaviorName.Select]: {
    option: {
      enabled: () => true,
    },
  },
  [SegmentBehaviorName.SelectAll]: {
    validator: (segment) => {
      // 不支持批量选中：转场元件
      if (isTransition(segment)) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        // 主视频坑位元件不能全选
        if (isTemplateMainVideoSegment(segment)) {
          return false
        }

        return true
      },
    },
  },
  [SegmentBehaviorName.Delete]: {
    option: {
      enabled: (segment) => {
        // 主视频坑位元件不能删除
        if (isTemplateMainVideoSegment(segment)) {
          return false
        }

        return true
      },
    },
  },
  [SegmentBehaviorName.Cut]: {
    validator: (segment) => {
      // 不允许裁剪：片段元件、Subtitle元件、模版元件
      if (
        isSegmentFragment(segment) ||
        isTemplateSegment(segment) ||
        isSegmentSubtitleGroup(segment) ||
        isSegmentDubbing(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: () => true,
    },
  },
  [SegmentBehaviorName.Drag]: {
    validator: (segment) => {
      // 不支持拖拽：转场元件、Subtitle元件、模版元件
      if (
        isTransition(segment) ||
        isTemplateSegment(segment) ||
        isSegmentSubtitleGroup(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: () => true,
    },
  },

  [SegmentBehaviorName.Copy]: {
    validator: (segment) => {
      // 不支持复制：转场元件、元件组、配音元件、Subtitle元件、模版元件
      if (
        isTransition(segment) ||
        isSegmentGroup(segment) ||
        isSegmentDubbing(segment) ||
        isTemplateSegment(segment) ||
        isSegmentSubtitleGroup(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },
  [SegmentBehaviorName.Paste]: {
    validator: (segment) => {
      // 不支持粘贴：转场元件、元件组、配音元件、Subtitle元件、模版元件
      if (
        isTransition(segment) ||
        isSegmentGroup(segment) ||
        isSegmentDubbing(segment) ||
        isTemplateSegment(segment) ||
        isSegmentSubtitleGroup(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Split]: {
    validator: (segment) => {
      if (
        isTransition(segment) ||
        isSegmentFragment(segment) ||
        isSegmentGroup(segment) ||
        isSegmentDubbing(segment) ||
        isTemplateSegment(segment) ||
        isSegmentSubtitleGroup(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.ToGroup]: {
    validator: (segment) => {
      if (
        [SegmentType.Video, SegmentType.Text, SegmentType.Audio].includes(
          segment.type,
        ) &&
        isConvertibleToSegmentGroup(segment)
      ) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (
          segment.deprecated ||
          isTemplateSegment(segment) ||
          isSegmentSubtitle(segment) ||
          isSegmentDubbing(segment)
        ) {
          return false
        }

        return true
      },
    },
  },

  /**
   * 设为片段
   * 条件：选中多个元件，且多选的元件中必须包含视频/图片元件，否则组合为片段功能禁用
   * 仅支持：除片段元件\转场元件其他所有类型元件
   */
  [SegmentBehaviorName.ToFragment]: {
    validator: (segment) => {
      if (
        isSegmentFragment(segment) ||
        isTransition(segment) ||
        isSegmentSubtitle(segment) ||
        isSegmentDubbing(segment) ||
        isSegmentSubtitleGroup(segment) ||
        isTemplateSegment(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  /**
   * 移入片段
   * 仅支持：非片段元件、转场元件，不包括元件组里面的子元件
   */
  [SegmentBehaviorName.MoveIn]: {
    validator: (segment) => {
      if (
        isSegmentFragment(segment) ||
        isTransition(segment) ||
        isSegmentSubtitle(segment) ||
        isSegmentDubbing(segment) ||
        isSegmentSubtitleGroup(segment) ||
        isTemplateSegment(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  /**
   * 移出片段
   * 仅支持：片段剧本元件，不包括元件组里面的子元件
   */
  [SegmentBehaviorName.MoveOut]: {
    validator: (segment) => {
      if (
        isSegmentFragment(segment) ||
        isTransition(segment) ||
        isSegmentSubtitle(segment) ||
        isSegmentDubbing(segment) ||
        isSegmentSubtitleGroup(segment) ||
        isTemplateSegment(segment)
      ) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || !isSegmentFragmentChild(segment)) {
          return false
        }

        return true
      },
    },
  },

  /**
   * 分离音频、还原音频
   * 仅支持：视频资源的视频元件
   */
  [SegmentBehaviorName.SeparateAudio]: {
    validator: (segment) => {
      if (isTemplateSegment(segment)) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        if (
          !isSegmentGroup(segment) &&
          segment.type === SegmentType.Video &&
          segment.material.type === AssetType.Video
        ) {
          return true
        }

        return false
      },
    },
  },

  [SegmentBehaviorName.Rate]: {
    validator: (segment) => {
      if (isSegmentDubbing(segment) || isTemplateSegment(segment)) {
        return false
      }

      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Animation]: {
    validator: (segment) => {
      if ([SegmentType.Text, SegmentType.Video].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Rect]: {
    validator: () => {
      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Shadow]: {
    validator: () => {
      return true
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Watermark]: {
    validator: (segment) => {
      if ([SegmentType.Sticker, SegmentType.Text].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.TextStyle]: {
    validator: (segment) => {
      if ([SegmentType.Text].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.AudioSetting]: {
    validator: (segment) => {
      if ([SegmentType.Audio, SegmentType.Video].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.AudioVoice]: {
    validator: (segment) => {
      if ([SegmentType.Audio, SegmentType.Video].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.AudioFade]: {
    validator: (segment) => {
      if (isSegmentDubbing(segment)) {
        return false
      }

      if ([SegmentType.Audio, SegmentType.Video].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (
          segment.deprecated ||
          isTemplateSegment(segment) ||
          isSegmentDubbing(segment)
        ) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.FilterSetting]: {
    validator: (segment) => {
      if ([SegmentType.Filter].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },

  [SegmentBehaviorName.Matting]: {
    validator: (segment) => {
      if ([SegmentType.Video, SegmentType.Sticker].includes(segment.type)) {
        return true
      }

      return false
    },
    option: {
      enabled: (segment) => {
        if (segment.deprecated || isTemplateSegment(segment)) {
          return false
        }

        return true
      },
    },
  },
}

export function defineSegmentBehavior(segment: SegmentCollection): void {
  const _define = (
    name: SegmentBehaviorName,
    option: DefineBehaviorOption<SegmentBehaviorOption>,
  ) => defineBehavior(segment, name, option)

  Object.entries(Behaviors).forEach(([name, { option }]) => {
    _define(name as SegmentBehaviorName, option)
  })
}

function defineBehavior(
  segment: SegmentCollection,
  name: SegmentBehaviorName,
  option: DefineBehaviorOption<SegmentBehaviorOption>,
): void {
  const behavior = segment.behavior ? segment.behavior : (segment.behavior = {})

  const optionPropertyDescriptors: Record<string, PropertyDescriptor> = {}
  for (const key in option) {
    optionPropertyDescriptors[key] = {
      get: () => {
        const result = option[key as keyof typeof option]?.(segment)

        if (key === 'enabled' && segment.disabled) {
          return false
        }

        return result
      },
      enumerable: true,
      configurable: true,
    }
  }

  const properties: SegmentBehaviorOption = (behavior[name] =
    {} as SegmentBehaviorOption)
  Object.defineProperties(properties, optionPropertyDescriptors)

  Object.defineProperty(behavior, name, {
    get() {
      // 根据元件维度，定制开关
      // see: https://alidocs.dingtalk.com/i/nodes/93NwLYZXWyg4ZYmRTEKjYyLbJkyEqBQm?utm_scene=person_space

      const validator = Behaviors[name]?.validator
      const isSupportBehavior = validator ? validator(segment) : true
      return isSupportBehavior ? properties : undefined
    },
    set() {
      //
    },
    enumerable: true,
    configurable: true,
  })
}
